import { useState } from 'react';
import { Link } from 'react-router-dom';
import { aboutData } from '@/mocks/lolita';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const avatarImages: Record<string, string> = {
  '小樱花': 'https://readdy.ai/api/search-image?query=Cute%20Asian%20girl%20portrait%2C%20soft%20smile%2C%20pastel%20pink%20background%2C%20editorial%20portrait%20photography%2C%20clean%20soft%20lighting&width=120&height=120&seq=avatar-xiaoyinghua&orientation=squarish',
  '暗夜蔷薇': 'https://readdy.ai/api/search-image?query=Elegant%20Asian%20woman%20portrait%2C%20gothic%20style%2C%20dark%20hair%2C%20cream%20background%2C%20editorial%20portrait%20photography%2C%20dramatic%20soft%20lighting&width=120&height=120&seq=avatar-anyeqiangwei&orientation=squarish',
  '古典蔷薇': 'https://readdy.ai/api/search-image?query=Mature%20Asian%20woman%20portrait%2C%20classic%20elegant%20style%2C%20warm%20smile%2C%20cream%20background%2C%20editorial%20portrait%20photography%2C%20soft%20warm%20lighting&width=120&height=120&seq=avatar-gudianqiangwei&orientation=squarish',
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 洛丽塔风格指南',
    description: aboutData.brandIntro,
    url: 'https://eachbill.cn/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '洛丽塔风格指南',
      url: 'https://eachbill.cn/',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://eachbill.cn/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://eachbill.cn/about' },
      ],
    },
    mainEntity: {
      '@type': 'Organization',
      name: '洛丽塔风格指南',
      url: 'https://eachbill.cn/',
      description: aboutData.brandIntro,
      foundingDate: '2020',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'hello@eachbill.cn',
      },
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢你的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    const formData = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9u430t3pcjqs2fcsll0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      if (res.ok && (parsed.code === 'OK' || !parsed.code)) {
        setFormStatus('success');
        setFormMessage('感谢你的留言，我们会尽快回复！');
        form.reset();
      } else {
        const meta = parsed?.meta as Record<string, string> | undefined;
        const errMsg = meta?.message || meta?.detail || text || '发送失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(String(errMsg));
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 洛丽塔风格指南"
        description={aboutData.brandIntro}
        keywords="洛丽塔,关于我们,洛丽塔风格指南"
        canonicalUrl="https://eachbill.cn/about"
        schemaData={aboutSchema}
      />

      <Navbar />

      <main className="pt-16">
        {/* Page Header */}
        <section className="relative h-[280px] md:h-[340px] flex items-end overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20Lolita%20fashion%20community%20gathering%2C%20group%20of%20girls%20in%20beautiful%20lace%20dresses%20at%20tea%20party%2C%20warm%20atmosphere%2C%20flowers%20tea%20cups%20pastries%2C%20cream%20and%20blush%20tones%2C%20editorial%20photography%2C%20soft%20natural%20lighting%2C%20friendship%20and%20joy&width=1400&height=500&seq=about-header-main&orientation=landscape"
            alt="关于洛丽塔风格指南"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-black/20"></div>
          <div className="relative z-10 container-custom pb-10 md:pb-14">
            <nav className="flex items-center gap-2 text-white/70 text-xs mb-4 flex-wrap">
              <Link to="/" className="hover:text-white transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white">关于我们</span>
            </nav>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-2">
              关于我们
            </h1>
            <p className="text-white/70 text-sm">了解洛丽塔风格指南的故事与使命</p>
          </div>
        </section>

        {/* Brand Intro */}
        <section className="section-padding bg-background-50">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-800 mb-5">
                我们的故事
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                {aboutData.brandIntro}
              </p>
              <p className="text-primary-500 font-heading text-lg italic">
                「{aboutData.mission}」
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-3xl mx-auto mb-14">
              {aboutData.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-6 bg-background-100 rounded-xl border border-background-200/70"
                >
                  <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                    {stat.value}
                  </p>
                  <p className="text-xs md:text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Values */}
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-800 text-center mb-10">
                我们的价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {aboutData.values.map((v) => (
                  <div
                    key={v.title}
                    className="flex gap-4 p-5 bg-background-100 rounded-xl border border-background-200/70"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-600 flex-shrink-0">
                      <i className={`${v.icon} text-lg`}></i>
                    </div>
                    <div>
                      <h4 className="font-heading text-base font-semibold text-foreground-800 mb-1.5">
                        {v.title}
                      </h4>
                      <p className="text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="section-padding bg-background-100">
          <div className="container-custom">
            <div className="text-center mb-12">
              <p className="text-primary-500 text-sm font-medium tracking-wider mb-2">口碑见证</p>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-800 mb-3">
                Lo娘们的真实评价
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto text-sm">
                来自洛丽塔社区的真实声音，每一位Lo娘的故事都是我们前进的动力
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
              {aboutData.testimonials.map((t) => (
                <div
                  key={t.name}
                  className="bg-background-50 rounded-xl p-6 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={avatarImages[t.name]}
                      alt={t.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{t.name}</p>
                      <p className="text-xs text-secondary-500">{t.style}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="section-padding bg-background-50">
          <div className="container-custom">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-10">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                  <i className="ri-mail-line text-2xl text-primary-500"></i>
                </div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-800 mb-3">
                  联系我们
                </h2>
                <p className="text-foreground-500 text-sm max-w-md mx-auto">
                  有任何问题、合作意向或只是想聊聊洛丽塔？欢迎随时给我们留言
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70"
              >
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                  aria-hidden="true"
                  className="form-honeypot"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all"
                      placeholder="你的名字"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all"
                    placeholder="留言主题"
                  />
                </div>

                <div className="mb-5">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
                    placeholder="请输入你想说的话（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                </div>

                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-all duration-300 text-sm whitespace-nowrap"
                >
                  发送留言
                </button>

                {formStatus !== 'idle' && (
                  <p
                    className={`text-sm text-center mt-4 ${
                      formStatus === 'success' ? 'text-green-600' : 'text-red-500'
                    }`}
                  >
                    {formMessage}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}